import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef } from 'react'
import s from './SectionLabel.module.css'

/**
 * Intertitre de section — les petites capitales bleues qui découpent une carte du
 * prototype (« Conclusion du contrôle », « Surfaces raccordées », « Origine »).
 *
 * Rendu en `<h2>`/`<h3>` par la prop `render` quand la section en est vraiment une dans le plan
 * du document ; sinon un `<p>`, ce qui est le cas le plus fréquent — découper visuellement une
 * carte n'ajoute pas toujours un niveau de titre.
 *
 * ⚠️ Ce n'est pas le `eyebrow` de `PageHeader` : celui-ci est gris et surmonte le `<h1>`
 * de l'écran ; celui-là est bleu et vit dans une carte. Deux rôles, deux composants.
 */
const sectionLabel = cva(s.root, {
  variants: {
    tone: {
      brand: s.toneBrand,
      muted: s.toneMuted,
    },
  },
  defaultVariants: { tone: 'brand' },
})

export type SectionLabelProps = useRender.ComponentProps<'p'> & VariantProps<typeof sectionLabel>

export function SectionLabel({ tone, render, className, ...props }: SectionLabelProps) {
  return useRender({
    render,
    defaultTagName: 'p',
    props: { className: sectionLabel({ tone, className }), ...props },
  })
}

/**
 * Complément en casse normale dans un intertitre — « · ensemble de la maison ». Sans lui,
 * la précision partirait en capitales espacées avec le reste, et se lirait comme un
 * second titre.
 */
export function SectionLabelNote({ className, ...props }: ComponentPropsWithRef<'span'>) {
  return <span className={className === undefined ? s.note : `${s.note} ${className}`} {...props} />
}
